import { FormEvent, useState } from "react";

export const contactModes = ["Rescue", "Implement", "Optimize", "Operate", "Not yet sure"] as const;
export type ContactMode = (typeof contactModes)[number];

export type ContactFields = {
  name: string;
  email: string;
  organization: string;
  role: string;
  area: string;
  mode: ContactMode | "";
  size: string;
  message: string;
};

export type ContactErrors = Partial<Record<keyof ContactFields, string>>;
export type ContactStatus = "idle" | "submitting" | "success";

const initialValues: ContactFields = {
  name: "",
  email: "",
  organization: "",
  role: "",
  area: "",
  mode: "",
  size: "",
  message: "",
};

function validate(v: ContactFields): ContactErrors {
  const errors: ContactErrors = {};
  if (!v.name.trim()) errors.name = "Enter your name.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email)) errors.email = "Enter a valid work email.";
  if (!v.organization.trim()) errors.organization = "Enter your organization.";
  if (!v.area) errors.area = "Select what this is about.";
  if (!v.mode) errors.mode = "Select the situation you’re in.";
  if (v.message.trim().length < 10) errors.message = "A sentence or two is enough.";
  return errors;
}

export function useContactForm(initial: Partial<ContactFields> = {}) {
  const startValues: ContactFields = { ...initialValues, ...initial };
  const [values, setValues] = useState<ContactFields>(startValues);
  const [errors, setErrors] = useState<ContactErrors>({});
  const [status, setStatus] = useState<ContactStatus>("idle");

  const update = <K extends keyof ContactFields>(key: K, value: ContactFields[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const next = validate(values);
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setStatus("submitting");
    await new Promise((r) => setTimeout(r, 1100));
    setStatus("success");
  };

  const reset = () => {
    setValues(startValues);
    setErrors({});
    setStatus("idle");
  };

  return { values, errors, status, update, submit, reset };
}
